import { RpcError } from "grpc-web";
import { CandidateServicePromiseClient } from "@/pb/scout/candidate/v1/candidate_grpc_web_pb";
import {
  CountRequest,
  SearchRequest,
  CountForAgentRequest,
  SearchForAgentRequest,
  VoteDownRequest,
  VoteUpRequest,
  CandidateSearchOption,
  CreateSearchOptionRequest,
  GetAllSearchOptionsOrderByUpdatedAtRequest,
  GetScoutCandidateDetailRequest,
  GetSearchOptionRequest,
  UpdateSearchOptionRequest,
  UpdateSearchOptionNameRequest,
  DeleteSearchOptionRequest,
  BulkVoteUpRequest
} from "@/pb/scout/candidate/v1/candidate_pb";
import { handleGrpcError } from "@/grpc-web/utils";

const client: CandidateServicePromiseClient = Object.assign(
  new CandidateServicePromiseClient(
    process.env.VUE_APP_SCOUT_GRPC_ORIGIN,
    null,
    {
      withCredentials: true
    }
  )
);

export const getCandidateCount = async (param: CountRequest) => {
  const response = await client.count(param).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return response.getCount();
};

export const getCandidates = async (param: SearchRequest) => {
  const response = await client.search(param).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return response.getUsersList();
};

export const getAgentCandidateCount = async (param: CountForAgentRequest) => {
  const response = await client.countForAgent(param).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return response.getCount();
};

export const getAgentCandidates = async (param: SearchForAgentRequest) => {
  const response = await client.searchForAgent(param).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return response.getUsersList();
};

export const voteUp = async (param: VoteUpRequest) => {
  const _ = await client.voteUp(param).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const voteDown = async (param: VoteDownRequest) => {
  const _ = await client.voteDown(param).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const bulkVoteUp = async (userIds: number[]) => {
  const request = new BulkVoteUpRequest();
  request.setUserIdsList(userIds);

  const _ = await client.bulkVoteUp(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const createSearchOption = async (
  name: string,
  searchOption: CandidateSearchOption
) => {
  const request = new CreateSearchOptionRequest();
  request.setSearchOption(searchOption);
  request.setSearchOptionName(name);

  const response = await client
    .createSearchOption(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return response.getId();
};

export const updateSearchOption = async (
  id: number,
  searchOption: CandidateSearchOption
) => {
  const request = new UpdateSearchOptionRequest();
  request.setSearchOption(searchOption);
  request.setId(id);

  await client.updateSearchOption(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });
};

export const updateSearchOptionName = async (id: number, name: string) => {
  const request = new UpdateSearchOptionNameRequest();
  request.setId(id);
  request.setName(name);

  await client.updateSearchOptionName(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });
};

export const deleteSearchOption = async (id: number) => {
  const request = new DeleteSearchOptionRequest();
  request.setId(id);

  await client.deleteSearchOption(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });
};

export const getAllSavedSearchOptions = async () => {
  const request = new GetAllSearchOptionsOrderByUpdatedAtRequest();

  const response = await client
    .getAllSearchOptionsOrderByUpdatedAt(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return response.getSavedCandidateSearchOptionsList();
};

export const getCandidateDetail = async (candidateUserId: number) => {
  const request = new GetScoutCandidateDetailRequest();
  request.setUserId(candidateUserId);

  const response = await client
    .getScoutCandidateDetail(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return response.getScoutCandidateDetail();
};

export const getSearchOption = async (templateId: number) => {
  const request = new GetSearchOptionRequest();
  request.setId(templateId);

  const response = await client
    .getSearchOption(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return response.getDetail();
};
